import _ from 'lodash';

import { mapGetters } from 'vuex';
import { loadStatus } from '@/types';

import loadGroupMixin from './loadGroupMixin';
import pluginMixin from './pluginMixin';
import unitsOfMeasurePlugin from './unitsOfMeasurePlugin';

export default {
  mixins: [
    loadGroupMixin,
    pluginMixin,
    unitsOfMeasurePlugin,
  ],
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('H:mm A MMM DD');
    },
  },
  computed: {
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    ...mapGetters('bookedLoads', {
      loadingBookedLoads: 'loadingLoads',
      bookedLoadsTime: 'timestamp',
      bookedLoads: 'bookedLoads',
      storeBookedLoadsCompanyId: 'companyId',
    }),
    bookedLoadsCompanyId() {
      return this.storeBookedLoadsCompanyId;
    },
  },
  methods: {
    bookedLoadStatus(load) {
      return _.get(load, 'loadStatus', null);
    },
    isBookedLoadScheduled(load) {
      return this.bookedLoadStatus(load) === loadStatus.SCHEDULED;
    },
    isBookedLoadInTransit(load) {
      return this.bookedLoadStatus(load) === loadStatus.IN_TRANSIT;
    },
    isBookedLoadDelivered(load) {
      return this.bookedLoadStatus(load) === loadStatus.DELIVERED;
    },
    isBookedLoadFinalized(load) {
      return this.bookedLoadStatus(load) === loadStatus.FINALIZED;
    },
    isBookedLoadCanceled(load) {
      return this.bookedLoadStatus(load) === loadStatus.CANCELED;
    },
    bookedLoadOwner(load) {
      return this.loadGroupOwner(load);
    },
    bookedLoadCustomer(load) {
      return this.loadGroupSponsor(load.tacticalInfo);
    },
    bookedLoadNickname(load) {
      const nicknameV1 = _.get(load, 'tacticalInfo.loadDetails.nickname');
      return this.loadGroupNickname(load) || nicknameV1;
    },
    bookedLoadBillOfLadingNumber(load) {
      return _.get(load, 'delivery.billOfLadingNumber');
    },
    // The LTN Account on the load. Mutually exclusive with vendor carrier company
    bookedLoadCarrierCompany(load) {
      return _.get(load, 'tacticalInfo.truckingCompany', {});
    },
    // The QB Vendor assigned the load.  Mutually exclusive with carrier company
    bookedLoadVendorCarrierCompany(load) {
      // undefined so it is easy to vendor || carrier
      return _.get(load, 'tacticalInfo.assignedVendor');
    },
    isBookedLoadVendorAssigned(load) {
      return !!this.bookedLoadVendorCarrierCompany(load);
    },
    // carrier || vendor carrier. useful for display and filtering
    bookedLoadHaulingCompany(load) {
      // not generally useful, as you can't tell wht kind of carrier you have. but useful for filtering
      return this.bookedLoadVendorCarrierCompany(load) || this.bookedLoadCarrierCompany(load);
    },
    bookedLoadAssignedTruckingCompanyDriverUserId(load) {
      return _.get(load, 'tacticalInfo.assignedTruckingCompanyDriverUserId', null);
    },
    bookedLoadAssignedTruckingCompanyDriverName(load) {
      return _.get(load, 'tacticalInfo.assignedTruckingCompanyDriverName', null);
    },
    bookedLoadDriverInfo(load) {
      return _.get(load, 'tacticalInfo.driverInfo');
    },
    bookedLoadRequiresDeliveryDocument(load) {
      return _.get(load, 'tacticalInfo.loadDetails.requirements.requireDeliveryDocument', null);
    },


    // this should really be load group short description
    bookedLoadShortDescription(load) {
      // handle both V1 and V2 loads
      const owner = _.get(load, 'tacticalInfo.loadDetails.listingCompany') ||
                    this.bookedLoadOwner(load);
      const customer = _.get(load, 'tacticalInfo.loadDetails.customerCompany') ||
                       owner;

      const customerName = customer ? customer.name : owner.name;

      const nickname = this.bookedLoadNickname(load);

      return nickname || customerName;
    },
    bookedLoadProgressIcon(load) {
      if (this.isBookedLoadCanceled(load)) return { color: 'accent', icon: 'cancel', description: 'Load has been Canceled' };
      if (this.isBookedLoadFinalized(load)) return { color: 'green', icon: 'check_circle_outline', description: 'Load has been closed out' };
      if (this.isBookedLoadScheduled(load)) return { color: 'accent', icon: 'schedule', description: 'Load is Scheduled for Pickup' };
      if (this.isBookedLoadInTransit(load)) return { color: 'accent', icon: 'local_shipping', description: 'Load is In Transit' };
      if (this.isBookedLoadDelivered(load)) return { color: 'accent', icon: 'done', description: 'Load has been Delivered' };

      return { icon: 'attach_money', color: 'accent', description: 'Load is in Settlement' };
    },
    // takes the load group participants and adds trucking company
    bookedLoadParticipantsByCompanyId(load) {
      const participants = this.loadGroupParticipantsByCompanyId(load);
      const truckingCompany = load.tacticalInfo.truckingCompany;
      if (truckingCompany && truckingCompany.id) {
        participants[truckingCompany.id] = truckingCompany;
      }

      return participants;
    },
    loadBookedLoads(companyId) {
      this.$store.dispatch('bookedLoads/getBookedLoads', companyId);
    },
    replaceBookedLoad(load) {
      this.$store.dispatch('bookedLoads/updateLoad', load);
    },
    removeLoad(loadId) {
      this.$store.dispatch('bookedLoads/removeLoad', loadId);
    },
    bookedLoadStyleClass(company, load) {
      // oh boy. this is a can of worms. really depends on the company viewing.

      // if the load dispatcher is looking at it:
      //   * QB Vendor/green: QB/Vendor dispatch (no LTN account)
      //   * LTN Account/yellow: outside truck (LTN account)
      //   * My Load + Unassigned/red: your truck, not assigned a driver
      //   * My Load + Assigned/light blue: your truck, assigned a driver, not accepted
      //   * My Load + Accepted/dark blue: your truck, assigned a driver, accepted

      // if the load trucking company is looking at it:
      //   * green: never. can't re-dispatch
      //   * yellow: never. can't re-dispatch
      //   * red: your truck, not assigned a driver
      //   * light blue: your truck, assigned a driver, not accepted
      //   * dark blue: your truck, assigned a driver, accepted

      // if a customer or facility is looking at it: no color

      let cssClass = '';
      if (this.isBookedLoadCarrierCompany(company, load)) {
        cssClass = 'unassigned';
        if (this.bookedLoadAssignedTruckingCompanyDriverUserId(load)) {
          cssClass = _.get(load, 'tacticalInfo.assignedTruckingCompanyDriverAccepted', false) ? 'assigned' : 'assigned_not_accepted';
        }
      }

      if (this.isBookedLoadOwnerCompany(company, load) || this.isBookedLoadOwnerCompanyLegacy(company, load)) {
        if (this.isBookedLoadVendorAssigned(load)) {
          cssClass = 'vendor_assigned';
        } else if (!this.isBookedLoadCarrierCompany(company, load)) {
          cssClass = 'not_mine';
        } else {
          cssClass = 'unassigned';
          if (this.bookedLoadAssignedTruckingCompanyDriverUserId(load)) {
            cssClass = _.get(load, 'tacticalInfo.assignedTruckingCompanyDriverAccepted', false) ? 'assigned' : 'assigned_not_accepted';
          }
        }
      }

      return cssClass;
    },
    isBookedLoadCarrierCompany(company, load) {
      const carrier = this.bookedLoadCarrierCompany(load);
      return carrier && carrier.id === company.id;
    },
    isBookedLoadOwnerCompany(company, load) {
      const deets = load.loadGroup || load;
      const owner = this.bookedLoadOwner(deets);
      return owner && owner.id === company.id;
    },
    isBookedLoadOwnerCompanyLegacy(company, load) {
      const owner = _.get(load, 'tacticalInfo.loadDetails.listingCompany', {});
      return owner && owner.id === company.id;
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/bookedLoadMixin.js